"use client";

import { useWeb3 } from "logic/components";
import { TxAction, TxStep, useContractStaticCall, useERC20Approve, useHandleTx, useTxSteps } from "logic/hooks";
import { fundWallet } from "logic/hooks/privy/fundWallet";
import { savePendingBridge } from "logic/pages/LoansPage/usePendingBridgeToast";
import { ERC20Metadata, OUSDaiUtilityAddresses } from "logic/utils";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { Address, zeroAddress } from "viem";
import { useBalance } from "wagmi";

import { OAdapterAbi, OUSDaiUtilityAbi } from "../../contracts";

import { useDstChainSP } from "./useDstChainSP";
import { useSrcChainSP } from "./useSrcChainSP";

export enum DepositType {
  Deposit,
}

export enum BuyActions {
  Approve,
  Buy,
}

interface useBuyTxParams {
  depositAmountUnits: bigint;
  depositToken: ERC20Metadata;
  recipient: Address;
  isBalanceInsufficient: boolean;
  mainChainId: number;
  enabled?: boolean;
}

export function useBuyTx(params: useBuyTxParams) {
  const { depositAmountUnits, depositToken, isBalanceInsufficient, mainChainId, recipient, enabled = true } = params;
  const depositType = DepositType.Deposit;

  const isBridging = depositToken.chainId !== mainChainId;
  const actionTitle = isBridging ? "Transfer" : "Transfer";

  const { address: account = zeroAddress } = useWeb3();
  const handleTx = useHandleTx();

  const { steps, setSteps, updateStepAtIndex, currentStep, currentStepIndex, resetSteps, isDone } =
    useTxSteps(getBuySteps());
  const { dstChainSP, lzReceiveGasLimit } = useDstChainSP({ recipient });
  const { srcChainSP, depositParamsData, depositParams, srcChainSendQuote, srcChainSendQuoteError } = useSrcChainSP({
    depositAmountUnits,
    depositToken,
    depositMainToken: depositToken.mainToken,
    depositType,
    dstChainSP,
    lzReceiveGasLimit,
    isBridging,
    enabled,
  });

  /**************************************************************************/
  /* Erc20 Approval */
  /**************************************************************************/
  const spender = isBridging ? (depositToken.OftAdapter ?? "0x") : OUSDaiUtilityAddresses[mainChainId];
  const {
    approvalQuery: { data: approvalData, error: approvalError },
    isApproved,
    allowanceQuery: { refetch: refetchAllowance },
  } = useERC20Approve({
    erc20: depositToken.address,
    spender,
    amount: depositAmountUnits,
    chainId: depositToken.chainId,
    enabled: Boolean(enabled && account !== zeroAddress && depositAmountUnits && !isBalanceInsufficient),
  });
  const canUpdateApproval = useRef(true);
  useEffect(() => {
    if (!canUpdateApproval.current) return;
    updateStepAtIndex(BuyActions.Approve, {
      status: depositAmountUnits > 0n && isApproved ? "confirmed" : "idle",
    });
  }, [updateStepAtIndex, isApproved, depositAmountUnits]);

  useEffect(() => {
    updateStepAtIndex(BuyActions.Buy, { action: actionTitle, title: actionTitle });
  }, [updateStepAtIndex, actionTitle]);

  /**************************************************************************/
  /* Withdraw/Deposit Transactions */
  /**************************************************************************/
  const value = depositParams.at(-1) as bigint;
  const isMainTxEnabled =
    enabled && isApproved && depositAmountUnits && account !== zeroAddress && !isBalanceInsufficient;

  const { data: utilityDepositTxData, error: utilityDepositTxError } = useContractStaticCall({
    abi: OUSDaiUtilityAbi,
    address: OUSDaiUtilityAddresses[mainChainId],
    functionName: "localCompose",
    value,
    args: [depositType, depositToken.address, depositAmountUnits, depositParamsData],
    chainId: mainChainId,
    query: {
      enabled: Boolean(!isBridging && isMainTxEnabled),
    },
  });

  const { data: sendTxData, error: sendTxError } = useContractStaticCall(
    srcChainSendQuote && {
      value: srcChainSendQuote.nativeFee,
      abi: OAdapterAbi,
      address: depositToken.OftAdapter,
      functionName: "send",
      args: [{ ...srcChainSP }, srcChainSendQuote, account],
      chainId: depositToken.chainId,
      query: {
        enabled: Boolean(isBridging && srcChainSendQuote && isMainTxEnabled),
      },
    },
  );

  /**************************************************************************/
  /* Native balance check + fund wallet (cross-chain only) */
  /**************************************************************************/
  const txValue = srcChainSendQuote?.nativeFee;

  const { data: nativeBalance } = useBalance({
    chainId: depositToken.chainId,
    address: account,
    query: {
      enabled: Boolean(enabled && isBridging && account !== zeroAddress),
    },
  });

  useEffect(() => {
    if (
      isBridging &&
      account !== zeroAddress &&
      txValue !== undefined &&
      nativeBalance !== undefined &&
      nativeBalance.value < txValue
    ) {
      const promise = fundWallet({
        address: account,
        amount: txValue * 3n,
        chainId: depositToken.chainId,
      });
      toast.promise(promise, {
        loading: "Funding wallet...",
        success: "Wallet funded",
        error: "Failed to fund wallet",
      });
    }
  }, [account, txValue, nativeBalance, depositToken.chainId, isBridging]);

  const [depositTxData, depositTxError] = isBridging
    ? [sendTxData, sendTxError]
    : [utilityDepositTxData, utilityDepositTxError];

  /**************************************************************************/
  /* Handlers */
  /**************************************************************************/
  function approve() {
    return handleTx({
      title: `Approve ${depositToken.symbol}`,
      params: approvalData?.request,
      updateStep: (u) => updateStepAtIndex(BuyActions.Approve, u),
      afterSigning: () => {
        canUpdateApproval.current = false;
      },
      afterConfirming: async (receipt) => {
        if (!receipt) canUpdateApproval.current = true;
        else await refetchAllowance();
      },
    });
  }

  function deposit() {
    return handleTx({
      title: `Deposit ${depositToken.symbol}`,
      params: depositTxData?.request,
      updateStep: (u) => updateStepAtIndex(BuyActions.Buy, u),
      afterSigning: (txHash) => {
        if (isBridging && txHash) {
          savePendingBridge({ txHash, srcChainId: depositToken.chainId, timestamp: Date.now() });
        }
      },
    });
  }

  /**************************************************************************/
  /* Actions */
  /**************************************************************************/
  const buyActions: TxAction[] = [
    {
      send: approvalData?.request && approve,
      error: approvalError,
    },
    {
      send: depositTxData?.request && deposit,
      error: depositTxError || sendTxError || srcChainSendQuoteError,
    },
  ];

  const action = buyActions[currentStepIndex];

  useEffect(() => {
    setSteps((prev) => prev.map((s, i) => ({ ...s, error: buyActions[i]?.error })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalError, depositTxError, sendTxError, srcChainSendQuoteError]);

  return {
    // Tx steps
    steps,
    currentStep,
    currentStepIndex,
    updateStepAtIndex,
    isDone,

    // Tx action
    action,
    resetTx: resetSteps,
  };
}

function getBuySteps(): TxStep[] {
  return [
    {
      title: `Approve`,
      action: "Approve",
      status: "idle",
    },
    { title: "Buy USDai", action: "Buy", status: "idle" },
  ];
}
